import type { FormRegistryValues } from '~/shared/forms/FormRegistryContext';
import type { NewCustomInterventionState, NewPredefinedInterventionState, SelectedWizardIntervention, WizardState } from './reducer/wizardReducer';
import {
	type AddGoalWithInterventionsCommand,
	type AddInterventionsCommand,
	type CustomGoalCommandModel,
	type CustomInterventionCommandModel,
	type FullGoalDefinitionViewModel,
	type PredefinedGoalCommandModel,
	type PredefinedInterventionCommandModel
} from '@generated';
import { convertClientValuesToServerValues } from '~/libraries/forms/values';
import { transformCustomInterventionForSave, transformPredefinedInterventionForSave } from '../util';
import type { FormValues } from '~/shared/forms/types';
import { isFullGoalDefinition } from '~/libraries/goals/util';

function getPredefinedGoalValues(formValues: FormValues, goalDefinition: FullGoalDefinitionViewModel): PredefinedGoalCommandModel {
	return {
		goalDefinitionId: goalDefinition.id,
		goalDefinitionValues: isFullGoalDefinition(goalDefinition)
			? convertClientValuesToServerValues(formValues, goalDefinition.definitionFormFields)
			: undefined
	};
}

function getCustomGoalValues(formValues: FormValues): CustomGoalCommandModel {
	return {
		subject: formValues.subject,
		definitionText: formValues.definitionText
	};
}

function getPredefinedInterventionValues(
	formRegisteryValues: FormRegistryValues,
	interventionState: NewPredefinedInterventionState
): PredefinedInterventionCommandModel {
	return transformPredefinedInterventionForSave(
		formRegisteryValues[interventionState.id],
		interventionState.id,
		interventionState.fullInterventionDefinition ?? interventionState.interventionDefinition
	);
}

function getCustomInterventionValues(
	formRegisteryValues: FormRegistryValues,
	interventionState: NewCustomInterventionState
): CustomInterventionCommandModel {
	return transformCustomInterventionForSave(formRegisteryValues, interventionState.id);
}

function getInterventionsToAttachIds(interventionsStates: SelectedWizardIntervention[]): string[] {
	return interventionsStates.filter(x => x.type === 'existing').map(x => x.existingIntervention.id);
}

export function transformWizardStateForSave(
	orderId: string,
	wizardState: WizardState,
	interventionFormRegisteryValues: FormRegistryValues
): AddGoalWithInterventionsCommand | AddGoalWithInterventionsCommand | AddInterventionsCommand {
	if (!wizardState.selectedGoal) {
		throw new Error('No goal selected');
	}
	const baseValues = {
		orderId,
		newPredefinedInterventions: wizardState.selectedInterventions
			.filter(x => x.type === 'predefined')
			.map(x => getPredefinedInterventionValues(interventionFormRegisteryValues, x)),
		newCustomInterventions: wizardState.selectedInterventions
			.filter(x => x.type === 'custom')
			.map(x => getCustomInterventionValues(interventionFormRegisteryValues, x))
	};

	switch (wizardState.selectedGoal.type) {
		case 'custom':
			if (!wizardState.selectedGoal.formValues) {
				throw new Error('No form values found for selected custom goal');
			}
			return {
				...baseValues,
				customGoal: getCustomGoalValues(wizardState.selectedGoal.formValues),
				interventionsToAttachIds: getInterventionsToAttachIds(wizardState.selectedInterventions)
			};
		case 'existing':
			return {
				...baseValues,
				goalId: wizardState.selectedGoal.existingGoal.id
			};
		case 'predefined':
			if (!wizardState.selectedGoal.goalDefinition.hasDefinitionFields) {
				return {
					...baseValues,
					predefinedGoal: {
						goalDefinitionId: wizardState.selectedGoal.goalDefinition.id
					},
					interventionsToAttachIds: getInterventionsToAttachIds(wizardState.selectedInterventions)
				};
			}
			if (!wizardState.selectedGoal.fullGoalDefinition) {
				throw new Error('No full goal definition found for selected predefined goal');
			} else if (!wizardState.selectedGoal.formValues) {
				throw new Error('No form values found for selected predefined goal');
			}
			return {
				...baseValues,
				predefinedGoal: getPredefinedGoalValues(wizardState.selectedGoal.formValues, wizardState.selectedGoal.fullGoalDefinition),
				interventionsToAttachIds: getInterventionsToAttachIds(wizardState.selectedInterventions)
			};
	}
}
